import React, { useEffect, useRef, useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import Modal from "react-bootstrap/Modal";
import OffCanvas from "../../components/offCanvas";
import { AddPGI } from "./AddPGI";
import EditIcon from "../../assets/img/EditTask.svg";
import Toaster, { ToastConfig } from "../../components/Toaster";
import { ErrorTypes } from "../../interfaces/errors";
import { EditGoal } from "./EditGoal";
import { EditIntervention } from "./EditIntervention";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import { GoalCommentRequest, InterventionCommentRequest, PatientGoalComment, PatientInterventionComment, ViewPatientGoalHistory, ViewPatientInterventionHistory, UpdateGoalRequest } from "pgi-experience-client";
import { createPgiApi } from "../../api-helpers/PgiApiHelper";
import { UserNameResolver, userCache } from "../../grid/resolvers/UserNameResolver";
import { formatDate } from "../../helper/dateFormatter";
import { workflowStatesCache } from "../../grid/renderers/WorkflowStateCellValueGetter";
import { useParams } from "react-router-dom";
import { LoadingHelper } from "../../components/loader/LoadingHelper";
import { NavDropdown } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { setPageTitle } from "../../store/slices/pageTitleSlice";
import "../../assets/scss/custom/_custom.scss";
import { Constants } from "../../constants/Lookups";
import { WorkflowConstants } from "../../helper/WorkflowConstants";
import { createWorkflowApi } from "../../api-helpers/CommonUtilsApiHelper";
import { WorkflowState } from "common-util-experience-client";
import fa from "@mobiscroll/react/dist/src/i18n/fa";
import { createGridApi } from "../../api-helpers/GridApiHelper";
import { PatientGoals, PatientIntervention, PatientPgiMapResponse } from "grid-experience-client";
import { createUsersApi } from "../../api-helpers/UserApiHelper";

export const calculateDayDifference = (date: Date) => {
  date = new Date(date);
  const currentDate = new Date();
  const timeDifference = date.getTime() - currentDate.getTime();
  const dayDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
  return dayDifference;
}

const pgiConstants = {
  CREATED_BY: "created by",
}

export const PGI = () => {
  const { patientId } = useParams<{ patientId: string }>();

  const [searchKey, setSearchKey] = useState('')
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [goalModalShow, setGoalModalShow] = React.useState(false);
  const [interventionModalShow, setInterventionModalShow] = React.useState(false);
  const [interventionHistoryModalShow, setInterventionHistoryModalShow] = React.useState(false);
  const [goalHistoryModalShow, setgoalHistoryModalShow] = React.useState(false);
  const [offCanvasShow, setOffCanvasShow] = useState(false);
  const [offCanvasComp, setOffCanvasComp] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [pgis, setPgis] = useState<PatientPgiMapResponse[]>([]);
  const [statusFilters, setStatusFilters] = useState<(string | undefined)[]>(['Needs Action', 'In-Progress']);
  const [filteredPgis, setFilteredPgis] = useState<PatientPgiMapResponse[]>([]);
  const [goalComment, setGoalComment] = useState<PatientGoalComment[]>([]);
  const [interventionComment, setInterventionComment] = useState<PatientInterventionComment[]>([]);
  const [goalHistory, setGoalHistory] = useState<ViewPatientGoalHistory[]>([]);
  const [interventionHistory, setInterventionHistory] = useState<ViewPatientInterventionHistory[]>([]);
  const [selectedGoal, setSelectedGoal] = useState<PatientGoals>();
  const [selectedIntervention, setSelectedIntervention] = useState<PatientIntervention>();
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    type: "success" as ErrorTypes,
    title: "",
    icon: "",
  });
  const [pgiUpdated, setPgiUpdated] = useState<boolean>(false);
  const parentGoalDate = useRef<Date>(new Date());
  const parentGoalId = useRef<string>("");

  const statusFiltersList = ['All', 'Needs Action', 'In-Progress', 'Done'];
  const usersApi = createUsersApi();

  const [goalCommentForm, setGoalCommentForm] = useState<GoalCommentRequest>({
    goalId: "",
    goalComment: '',
  });
  const dispatch = useDispatch();

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKey(e.target.value);

    const searchText = e.target.value.toLowerCase();
    if (searchText === '') {
      setFilteredPgis(pgis);
      setActiveKeys([]);
      return;
    }

    const filteredData = pgis.filter(
      (pgi: PatientPgiMapResponse) =>
        pgi.problem?.problem.toLowerCase().includes(searchText) ||
        pgi.goal.find(
          (goal) =>
            goal.goal?.goal.toLowerCase().includes(searchText) ||
            goal.interventions.find((intervention) =>
              intervention.intervention?.intervention
                .toLowerCase()
                .includes(searchText)
            )
        )
    );
    setFilteredPgis(filteredData);

    const newActiveKeys = filteredData.flatMap((pgi, pgiIndex) => {
      const problemKey = `problem-${pgiIndex}`;
      const goalKeys = pgi.goal.flatMap((goal, goalIndex) => {
        if (
          goal.goal?.goal.toLowerCase().includes(searchText) ||
          goal.interventions.some((intervention) =>
            intervention.intervention?.intervention
              .toLowerCase()
              .includes(searchText)
          )
        ) {
          return `problem-${pgiIndex}__goal-${goalIndex}`;
        }
        return [];
      });

      return [problemKey, ...goalKeys];
    });
    setActiveKeys(newActiveKeys);
  };

  const handleAccordionToggle = (key: string) => {
    if (activeKeys.includes(key)) {
      setActiveKeys(activeKeys.filter((activeKey) => activeKey !== key));
    } else {
      setActiveKeys([...activeKeys, key]);
    }
  };

  const applyFilter = (filterValue?: string, defaultPgi?: PatientPgiMapResponse[]) => {

    let updatedStatusFilters: (string | undefined)[] = [];
    if (filterValue) {

      if (filterValue === 'All') {
        updatedStatusFilters = ['All'];
      } else {
        updatedStatusFilters = statusFilters.includes(filterValue!)
          ? statusFilters.filter(filter => filter !== filterValue) // Remove the filter
          : [...statusFilters, filterValue];

        if (updatedStatusFilters.length !== statusFiltersList.length - 1) {
          updatedStatusFilters = updatedStatusFilters.filter(filter => filter !== 'All');
        }
        const otherFilters = statusFiltersList.filter(status => status !== 'All');
        const allDeselected = otherFilters.every(filter => !updatedStatusFilters.includes(filter));

        if (allDeselected) {
          updatedStatusFilters = ['All'];
        }
      }
      setStatusFilters(updatedStatusFilters);
    }
    let currentPgis = pgis
    if (defaultPgi?.length) {
      updatedStatusFilters = statusFilters
      currentPgis = defaultPgi
    }
    if (updatedStatusFilters.length === 0 || updatedStatusFilters.includes('All')) {
      setFilteredPgis(pgis);
      setActiveKeys([]);
      return;
    }
    const problems = [] as PatientPgiMapResponse[];
    currentPgis.forEach((p) => {
      let newProblem = p;
      const goals = [] as PatientGoals[];
      p.goal.forEach((g) => {
        let newGoal = g;
        const filteredIntervention = g.interventions.filter((i) => {
          return updatedStatusFilters.includes(i.status);
        });
        newGoal = { ...g, interventions: filteredIntervention };
        goals.push(newGoal);
      });
      const filteredGoals = goals.filter((g) => {
        return updatedStatusFilters.includes(g.status) || g.interventions.length > 0;
      })
      newProblem = { ...p, goal: filteredGoals }
      problems.push(newProblem);
    });
    const filteredData = problems.filter((p) => {
      return updatedStatusFilters.includes(p.problemStatus) || p.goal.length > 0;
    })


    setFilteredPgis(filteredData);

    const newActiveKeys = filteredData.flatMap((pgi, pgiIndex) => {
      const problemKey = `problem-${pgiIndex}`;
      const problemMatchesFilter = updatedStatusFilters.includes(pgi.problemStatus);

      const goalKeys = pgi.goal.flatMap((goal, goalIndex) => {
        const goalMatchesFilter = updatedStatusFilters.includes(goal.status);
        const interventionMatchesFilter = goal.interventions.some(intervention =>
          updatedStatusFilters.includes(intervention.status)
        );

        if (goalMatchesFilter || interventionMatchesFilter) {
          return `problem-${pgiIndex}__goal-${goalIndex}`;
        }
        return [];
      });

      return problemMatchesFilter || goalKeys.length > 0 ? [problemKey, ...goalKeys] : [];
    });

    setActiveKeys(newActiveKeys);
  }

  const handleGoalInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setGoalCommentForm((prevState: GoalCommentRequest) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  }

  const goalCommentSubmit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setGoalModalShow(false);
    try {
      await pgiApi.pgiControllerAddGoalComment(goalCommentForm);
      getGoalComment(goalCommentForm.goalId);
      getPgi();
    } catch (error) {
      console.error(error);
    }
    finally {
      setGoalCommentForm({ ...goalCommentForm, goalComment: '' })
    }
  }

  const [interventionCommentForm, setInterventionCommentForm] = useState<InterventionCommentRequest>({
    interventionId: "",
    interventionComment: '',
  })

  const handleInterventionInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInterventionCommentForm((prevState: InterventionCommentRequest) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  }

  const interventionCommentSubmit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setInterventionModalShow(false);
    try {
      await pgiApi.pgiControllerAddInterventionComment(interventionCommentForm);
      getInterventionComment(interventionCommentForm.interventionId);
      getPgi();
    } catch (error) {
      console.error(error);
    } finally {
      setInterventionCommentForm({ ...interventionCommentForm, interventionComment: '' })
    }
  }

  const pgiApi = createPgiApi();
  const gridApi = createGridApi();

  const getPgi = async () => {
    LoadingHelper.showLoader(".pgi-view");
    try {
      const pgiResponse = await gridApi.gridControllerGetPatientPgiMap(
        patientId!
      );
      const pgiResult = pgiResponse.data;
      if (pgiResult) {
        await setPgis(pgiResult);
        await setFilteredPgis(pgiResult);
        applyFilter(undefined, pgiResult)
      }
    } catch (error) {
      console.log(error);
    } finally {
      LoadingHelper.hideLoader(".pgi-view");
    }
  };
  const handlerAddUpdatePgiForm = async (toasterConfig: ToastConfig) => {
    setToastConfig(toasterConfig);
    setShowToast(true);
    setPgiUpdated(true);
  };

  const getGoalComment = async (patientGoalId: string) => {
    const goalCommentResponse = await pgiApi.pgiControllerGetGoalComments(patientGoalId);
    const goalCommentData = goalCommentResponse.data;
    setGoalComment(goalCommentData);
  }

  const getInterventionComment = async (patientInterventionId: string) => {
    if (patientInterventionId === '') {
      return
    }
    const interventionCommentResponse = await pgiApi.pgiControllerGetInterventionComments(patientInterventionId);
    const interventionCommentData = interventionCommentResponse.data;
    setInterventionComment(interventionCommentData);
  }

  const getGoalHistory = async (patientGoalId: string) => {
    if (patientGoalId === '') {
      return
    }
    const goalHistoryResponse = await pgiApi.pgiControllerGetGoalHistories(patientGoalId);
    const goalHistoryData = goalHistoryResponse.data;
    goalHistoryData.map((goalRecord) => {
      if (goalRecord.fieldChanged === 'assignee') {
        UserNameResolver(goalRecord.oldValue);
        UserNameResolver(goalRecord.newValue);
      }
    });
    setGoalHistory(goalHistoryData);
    setTimeout(()=>setgoalHistoryModalShow(true), 1000); // giving time to load user data
  };

  const getInterventionHistory = async (patientInterventionId: string) => {
    if (patientInterventionId === '') {
      return
    }
    const interventionHistoryResponse =
      await pgiApi.pgiControllerGetInterventionHistories(patientInterventionId);
    const interventionData = interventionHistoryResponse.data;

    interventionData.map((invHistory) => {
      if (invHistory.fieldChanged === 'assignee') {
        UserNameResolver(invHistory.oldValue);
        UserNameResolver(invHistory.newValue);
      }
    })
    setInterventionHistory(interventionData);
    setTimeout(()=>setInterventionHistoryModalShow(true), 1000); // giving time to load user data
  };

  useEffect(() => {
    dispatch(setPageTitle('PGI: Patient Profile | Spire360'));
    getPgi();
  }, []);

  useEffect(() => {
    if (!pgiUpdated) return;
    const toastConfig: ToastConfig = {
      type: "interactive",
      title: "",
      icon: "info",
      region: 'bottom-right',
      closeButton: false,
      autohide: false,
      acceptCallback: acceptCallback,
      acceptBtnText: "Yes, Set to Done"
    }
    if (offCanvasComp == "editIntervention") {
      toastConfig.title = "All Interventions are completed";
      pgis.forEach((problem: PatientPgiMapResponse) => {
        var toastDisplayed: boolean = false;
        problem.goal.forEach((goal: PatientGoals) => {
          var allInterventionsCompleted: boolean = true;
          var goalFound: boolean = false;
          goal.interventions.forEach((intervention: PatientIntervention) => {
            if (intervention.patientInterventionId === selectedIntervention?.patientInterventionId) {
              toastConfig.message = `Do you want to close the Goal: ${goal.goal?.goal}?`;
              goalFound = true;
              parentGoalDate.current = goal.dueDate;
              parentGoalId.current = goal.patientGoalId;
            };
            if (intervention.status !== Constants.DONE_STATUS) {
              allInterventionsCompleted = false;
              return;
            }
          });
          if (goalFound) {
            if (allInterventionsCompleted) {
              toastDisplayed = true;
              setToastConfig(toastConfig);
              setShowToast(true);
            }
            return;
          };
        })
        if (toastDisplayed) return;
      });
    }
    setPgiUpdated(false);
  }, [filteredPgis])

  const acceptCallback = async () => {
    LoadingHelper.showLoader(".pgi-view");
    const workflow = createWorkflowApi();
    const workflowStatusResponse = await workflow.workflowControllerGetStatesForWorkflow(WorkflowConstants.goal);
    const doneGoalId = workflowStatusResponse.data.filter((state: WorkflowState) => state.stateName === Constants.DONE_STATUS)[0].stateId;
    const updatedGoal: UpdateGoalRequest = {
      status: doneGoalId,
      dueDate: parentGoalDate.current,
      goalComment: "",
      goalCompletedOn: new Date()
    };
    await pgiApi.pgiControllerUpdateGoal(updatedGoal, parentGoalId.current);
    const toasterConfig: ToastConfig = {
      type: "success",
      title: "Goal updated successfully!",
      icon: "check_circle_outline",
    };
    setToastConfig(toasterConfig);
    setShowToast(true);
    getPgi();
  }

  const dueDateBadge = (date: Date) => {
    const dayDifference = calculateDayDifference(date);

    if (dayDifference === 0) {
      return <span className="badge background-planned">Today</span>;
    } else if (dayDifference === 1) {
      return <span className="badge background-noshow">Tomorrow</span>;
    } else if (dayDifference > 1 && dayDifference <= 7) {
      return <span className="badge background-completed">{dayDifference}d</span>;
    } else if (dayDifference < 0) {
      return <span className="badge background-cancelled">Delay: {Math.abs(dayDifference)}d</span>;
    } else {
      return <span className="badge background-completed">{dayDifference}d</span>;
    }
  }

  const overdueDateTextFormat = (date: Date) => {
    const dayDifference = calculateDayDifference(date);
    date = new Date(date);

    if (dayDifference < 0) {
      return <span className="text-danger">{date.toLocaleDateString()}</span>;
    }
    else return <span className="">{date.toLocaleDateString()}</span>;
  }

  return (
    <>
      <div className="pgi-view row">
        <Toaster
          show={showToast}
          onClose={() => setShowToast(false)}
          type={toastConfig.type}
          title={toastConfig.title}
          message={toastConfig.message}
          icon={toastConfig.icon}
          region={toastConfig.region}
          closeButton={toastConfig.closeButton}
          autohide={toastConfig.autohide}
          iconStyle={toastConfig.iconStyle}
          acceptCallback={toastConfig.acceptCallback}
          acceptBtnText={toastConfig.acceptBtnText}
          addClass={`mb-5 py-1 h-fit-content`}
        />
      </div>
      <div className="tabs__content">
        <div className="d-md-flex align-items-center pb-3 w-full justify-content-end" aria-label="breadcrumb">
          <div className="flex-fill d-flex align-items-center">
            <div>
              <h1 className="h5 m-0">Problem</h1>
            </div>
          </div>
          <div className="d-flex align-items-center">
            <div className="my-auto mt-2 mt-md-0 me-2 me-md-0">
              <div className="search-box">
                <span className="material-symbols-outlined search-box__icon ">
                  search
                </span>
                <input
                  name="problem"
                  type="text"
                  id="searchInput"
                  placeholder="Search PGI"
                  value={searchKey}
                  onChange={handleSearch}
                  className="form-control search-box__input fw-normal fs-14"
                  style={{ padding: '0.2rem 0 0.2rem 2.25rem' }}
                />
              </div>
            </div>
            <NavDropdown
              id="lookup-dropdown-label"
              title="Status"
              align="end"
              className={`btn-dropdown ms-md-2 me-2 mt-2 mt-md-0 ${statusFilters.length ? 'border border-primary rounded-2' : 'border border-white'}`}
            >

              <div>
                <div className={`filter-container py-1`}>
                  {statusFiltersList?.map((statusFilter, index) => (
                    <div key={index}>
                      <label className="collapse-item d-flex px-3 py-1">
                        <input
                          className="form-check-input me-2"
                          type="checkbox"
                          name={`status-${index}`}
                          checked={statusFilter === "All" ?
                            statusFilters.includes('All') : statusFilters.includes(statusFilter)}
                          onChange={() => {
                            applyFilter(statusFilter)
                          }}
                        />
                        <div className="fs-13">{statusFilter}</div>
                      </label>
                    </div>

                  ))}                </div>
              </div>
            </NavDropdown>
            <button
              className="btn btn-sm btn-secondary px-3 mt-2 mt-md-0 rounded-pill pe-4 text-nowrap"
              onClick={() => {
                setOffCanvasShow(!offCanvasShow);
                setOffCanvasComp("addPGI");
              }}
            >
              <span className="material-symbols-outlined fs-16 align-text-top">
                add
              </span> Add PGI
            </button>
          </div>
        </div>
        <div className="pgi-table border rounded fs-12">
          <div className="d-flex bg-gray-FAF fw-bold">
            <div className="min-w-50px py-2 text-center"></div>
            <div className="w-flex-lg flex-fill py-2 px-3 border-start">
              <strong>PROBLEM - GOAL - INTERVENTION</strong>
            </div>
            <div className="min-w-100px p-2 border-start text-center">
              Created On
            </div>
            <div className="min-w-200px p-2 border-start text-center">Due Date</div>
            <div className="min-w-200px p-2 border-start text-center">
              Completed on
            </div>
            <div className="min-w-200px p-2 border-start text-center">Status</div>
            <div className="min-w-50px py-2 border-start text-center">Edit</div>
          </div>
          {!filteredPgis.length && (
            <div className="no-records-message d-flex justify-content-center align-items-center">
              <div className="text-center">
                No records to show
              </div>
            </div>
          )}
          <Accordion activeKey={activeKeys} alwaysOpen>
            {filteredPgis?.map((pgi: PatientPgiMapResponse, index: number) => {
              return (
                <Accordion.Item key={index} eventKey={`problem-${index}`}>
                  <div className="d-flex align-items-stretch border-top fs-12 problem-heading">
                    <div className="min-w-50px py-2 text-center"></div>
                    <div className="w-flex-lg flex-fill py-2 px-4 border-start">
                      <Accordion.Header
                        onClick={async () => {
                          handleAccordionToggle(`problem-${index}`);
                          const goal = pgi.goal.find((goal) => goal.patientGoalId);
                          getGoalComment(goal ? goal?.patientGoalId : "");
                        }}
                      >
                        <a
                          data-bs-toggle="collapse"
                          href="#problem-001"
                          className="accordion-link"
                        >
                          <strong className="d-block text-dark fw-bold">
                            Problem {index + 1}: {pgi.problem?.problem}
                          </strong>
                          {
                            <span className="d-block fs-11 text-light">
                              {pgi.problem?.problemDesc}
                            </span>
                          }
                        </a>
                      </Accordion.Header>
                    </div>
                    <div className="min-w-100px border-start d-flex align-items-center justify-content-center">
                      {pgi.createdAt &&
                        formatDate(pgi.createdAt.toString().slice(0, 10))}
                    </div>
                    <div className="min-w-200px border-start d-flex align-items-center justify-content-center">
                      {"-"}
                    </div>
                    <div className="min-w-200px border-start d-flex align-items-center justify-content-center">
                      {pgi.problemCompletedOn && formatDate(pgi.problemCompletedOn.toString())}
                    </div>
                    <div className="min-w-200px border-start d-flex align-items-center justify-content-center">
                      {pgi.problemStatus}
                    </div>
                    <div className="min-w-50px border-start d-flex align-items-center justify-content-center"></div>
                  </div>
                  <Accordion.Body className="p-0">
                    {pgi.goal &&
                      pgi.goal?.map((goal: PatientGoals, goalIndex: number) => (
                        <Accordion.Item
                          key={goalIndex}
                          eventKey={`problem-${index}__goal-${goalIndex}`}
                        >
                          <div className="d-flex align-items-stretch border-top fs-12">
                            <div className="d-flex align-items-center justify-content-center min-w-50px py-2 text-center">
                              <a
                                href="#"
                                onClick={async () => {
                                  setSelectedGoal(goal);
                                  getGoalHistory(goal.patientGoalId || '');
                                }}
                              >
                                <span className="material-symbols-outlined fs-20 text-secondary">
                                  history
                                </span>
                              </a>
                            </div>
                            <div className="w-flex-lg d-flex flex-fill align-items-center py-2 px-3 ps-12 border-start">
                              <div className="flex-fill ps-1">
                                <div>
                                  <Accordion.Header
                                    onClick={() => {
                                      handleAccordionToggle(
                                        `problem-${index}__goal-${goalIndex}`
                                      );
                                      getGoalComment(goal.patientGoalId || "");
                                      const intervention = goal?.interventions.find((intervention) => intervention.patientInterventionId);
                                      getInterventionComment(intervention ? intervention?.patientInterventionId : "");
                                    }}
                                  >
                                    <a
                                      href="#"
                                      className="accordion-link"
                                    >
                                      <strong className="d-inline-block me-2">
                                        <span className="text-light-black fw-600">
                                          Goal {goalIndex + 1}: {goal.goal?.goal}
                                        </span>
                                      </strong>
                                      {/* <span className="badge badge-success me-2">
                                        Housing{" "}
                                        //@Todo : Fetch badge from api{" "}
                                      </span> */}
                                    </a>
                                  </Accordion.Header>
                                </div>
                              </div>
                              <div>
                                <OverlayTrigger
                                  trigger={["hover", "focus"]}
                                  placement="right"
                                  overlay={goalPopover(goalComment)}
                                  onEntering={async () => {
                                    await getGoalComment(goal?.patientGoalId || "")
                                  }}
                                >
                                  <a
                                    href="#"
                                    className="d-flex align-items-center text-nowrap"
                                    onClick={async () => {
                                      await getGoalComment(goal.patientGoalId || '');
                                      setSelectedGoal(goal);
                                      setGoalCommentForm({
                                        ...goalCommentForm,
                                        goalId: goal.patientGoalId || '',
                                      });
                                      setGoalModalShow(true);

                                    }}
                                  >
                                    <span className={`material-symbols-outlined fs-18 align-text-top text-secondary`}>
                                      comment
                                    </span>
                                    <span className="text-secondary">{goal.goalCommentCount}</span>
                                    {/* <span className="circle-ico n circle-icon-sm bg-teal-light border-transparent fw-semibold ms-2">
                                      MO
                                    </span> */}
                                    {/* <span className="circle-icon circle-icon-sm bg-teal-light border-transparent fw-semibold ms-2">
                                      <img
                                        src="https://cdn-icons-png.flaticon.com/512/168/168719.png"
                                        alt="Martina Olivera"
                                        className="img-fluid"
                                      />
                                    </span>
                                    <span className="circle-icon circle-icon-sm bg-teal-light border-transparent fw-semibold ms-2">
                                      <img
                                        src="https://cdn-icons-png.flaticon.com/512/168/168719.png"
                                        alt="Martina Olivera"
                                        className="img-fluid"
                                      />
                                    </span> */}
                                  </a>
                                </OverlayTrigger>
                              </div>
                            </div>
                            <div className="min-w-100px border-start d-flex align-items-center justify-content-center">
                              {goal.createdAt &&
                                formatDate(
                                  goal.createdAt.toString().slice(0, 10)
                                )}
                            </div>
                            <div className="min-w-200px border-start d-flex gap-2 align-items-center justify-content-center">
                              {goal.dueDate && formatDate(goal.dueDate)}
                              <span className="fs-9">
                                {dueDateBadge(goal.dueDate)}
                              </span>
                            </div>
                            <div className="min-w-200px border-start d-flex align-items-center justify-content-center">
                              {goal.goalCompletedOn &&
                                formatDate(goal.goalCompletedOn.toString())}
                            </div>
                            <div className="min-w-200px border-start d-flex align-items-center justify-content-center">
                              <span className="text-secondary">
                                {goal.status}
                              </span>
                            </div>
                            <div className="min-w-50px border-start d-flex align-items-center justify-content-center">
                              {(
                                <a
                                  href="#"
                                  onClick={async () => {
                                    setOffCanvasShow(!offCanvasShow);
                                    setOffCanvasComp("editGoal");
                                    setSelectedGoal(goal);
                                  }}
                                >
                                  <span>
                                    <img src={EditIcon} alt="edit Icon" />
                                  </span>
                                </a>
                              )}
                            </div>
                          </div>
                          <Accordion.Body className="p-0">
                            {goal &&
                              goal.interventions?.map(
                                (
                                  intervention: PatientIntervention,
                                  interventionIndex: number
                                ) => (
                                  <div
                                    key={interventionIndex}
                                    className="d-flex align-items-stretch border-top fs-12"
                                  >
                                    <div className="min-w-50px py-2 text-center">
                                      <a
                                        href="#"
                                        onClick={async () => {
                                          setSelectedIntervention(intervention);
                                          getInterventionHistory(intervention.patientInterventionId || '');
                                        }}
                                      >
                                        <span className="material-symbols-outlined fs-20 text-secondary">
                                          history
                                        </span>
                                      </a>
                                    </div>
                                    <div className="w-flex-lg d-flex flex-fill align-items-center py-2 px-3 border-start ps-12">
                                      <div className="flex-fill ps-6">
                                        <div>
                                          <span className="intervention-badge badge badge-offwhite me-1 border">
                                            Intervention
                                          </span>
                                          <span className="badge badge-light me-1">
                                            {/* Low */}
                                          </span>
                                          <span className="me-2 text-light-black">
                                            {intervention.intervention?.intervention}
                                          </span>
                                        </div>
                                      </div>
                                      <div>
                                        <OverlayTrigger
                                          trigger={["hover", "focus"]}
                                          placement="right"
                                          overlay={interventionPopover(interventionComment)}
                                          onEntering={async () => {
                                            await getInterventionComment(intervention?.patientInterventionId || "")
                                          }}
                                        >
                                          <a
                                            href="#"
                                            className="d-flex align-items-center text-nowrap"
                                            onClick={async () => {
                                              await getInterventionComment(intervention.patientInterventionId || '');
                                              setSelectedIntervention(intervention);
                                              setInterventionCommentForm({
                                                ...interventionCommentForm,
                                                interventionId: intervention.patientInterventionId || '',
                                              });
                                              setInterventionModalShow(true);
                                            }}
                                          >
                                            <span className="material-symbols-outlined fs-18 align-text-top text-secondary">
                                              comment
                                            </span>
                                            <span className="text-secondary">
                                              {intervention.interventionCommentCount}
                                            </span>
                                            {/* <span className="circle-icon circle-icon-sm bg-teal-light border-transparent fw-semibold ms-2">
                                              MO
                                            </span>
                                            <span className="circle-icon circle-icon-sm bg-teal-light border-transparent fw-semibold ms-2">
                                              <img
                                                src="https://cdn-icons-png.flaticon.com/512/168/168719.png"
                                                alt="Martina Olivera"
                                                className="img-fluid"
                                              />
                                            </span>
                                            <span className="circle-icon circle-icon-sm bg-teal-light border-transparent fw-semibold ms-2">
                                              <img
                                                src="https://cdn-icons-png.flaticon.com/512/168/168719.png"
                                                alt="Martina Olivera"
                                                className="img-fluid"
                                              />
                                            </span> */}
                                          </a>
                                        </OverlayTrigger>
                                      </div>
                                    </div>
                                    <div className="min-w-100px border-start d-flex align-items-center justify-content-center">
                                      {intervention.createdAt &&
                                        formatDate(
                                          intervention.createdAt.toString().slice(0, 10)
                                        )}
                                    </div>
                                    <div className="min-w-200px border-start d-flex align-items-center justify-content-center text-success">
                                      <div className="text-center d-flex gap-2 px-2">
                                        <div>
                                          {intervention.dueDate &&
                                            formatDate(intervention.dueDate)}
                                        </div>
                                        <span className="fs-9">
                                          {/* On Time {@TODO:} */}{
                                            dueDateBadge(intervention.dueDate)
                                          }
                                        </span>
                                      </div>
                                    </div>
                                    <div className="min-w-200px border-start d-flex align-items-center justify-content-center text-success">
                                      {intervention.interventionCompletedOn &&
                                        formatDate(
                                          intervention.interventionCompletedOn.toString()
                                        )}
                                    </div>
                                    <div className="min-w-200px border-start d-flex align-items-center justify-content-center">
                                      <span className="text-success">
                                        {intervention.status}
                                      </span>
                                    </div>
                                    <div className="min-w-50px border-start d-flex align-items-center justify-content-center">
                                      {(
                                        <a
                                          href="#"
                                          onClick={() => {
                                            setOffCanvasShow(!offCanvasShow);
                                            setOffCanvasComp("editIntervention");
                                            setSelectedIntervention(intervention);
                                          }}
                                        >
                                          {" "}
                                          <span>
                                            <img
                                              src={EditIcon}
                                              alt="edit Icon"
                                            />
                                          </span>
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                )
                              )}
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </div>
      </div>
      {/* Goal Comment  */}
      <Modal
        show={goalModalShow}
        onHide={() => setGoalModalShow(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <>
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              Comments
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h3 className="fs-16">Goal : {selectedGoal?.goal?.goal}</h3>
            {goalComment?.map(
              (comment: PatientGoalComment, commentIndex: number) => (
                <div className="border rounded mt-4" key={commentIndex}>
                  <div className="d-flex align-items-start px-3 py-2 fs-14">
                    <div>
                      <span className="material-symbols-outlined fs-20 me-1 mt-1 text-secondary">
                        comment
                      </span>
                    </div>
                    <div className="flex-fill">{comment.comment}</div>
                  </div>

                  <div className="d-flex bg-gray-FAF justify-content-between fs-12 px-3 py-2">
                    <div className="text-light">
                      {formatDate(comment.createdAt.toString().slice(0, 10))}
                    </div>
                    <div className="d-flex align-items-center text-base">
                      <span className="material-symbols-outlined fs-18 me-1">
                        person
                      </span>
                      <span>{UserNameResolver(comment.createdBy)}</span>
                    </div>
                  </div>
                </div>
              )
            )}

            <div className="mt-4">
              <label
                htmlFor="newNotes"
                className="form-label fs-12 fw-semibold mb-1"
              >
                New Comments:
              </label>
              <textarea
                name="goalComment"
                className="form-control form-control-sm"
                id="goalComment"
                placeholder="Write here..."
                rows={2}
                onChange={(e) => handleGoalInputChange(e)}
              ></textarea>
            </div>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary rounded-pill px-6 my-0"
              onClick={() => setGoalModalShow(false)}
            >
              Discard
            </button>
            <button
              type="button"
              className="btn btn-sm btn-secondary rounded-pill px-6 my-0"
              onClick={(e) => goalCommentSubmit(e)}
              disabled={goalCommentForm?.goalComment === '' ? true : false}
            >
              Submit
            </button>
          </Modal.Footer>
        </>
      </Modal>

      {/* Intervention Comment */}
      <Modal
        show={interventionModalShow}
        onHide={() => setInterventionModalShow(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <>
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              Comments
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h3 className="fs-16">
              Intervention : {selectedIntervention?.intervention?.intervention}
            </h3>
            {interventionComment?.map(
              (comment: PatientInterventionComment, commentIndex: number) => (
                <div className="border rounded mt-4" key={commentIndex}>
                  <div className="d-flex align-items-start px-3 py-2 fs-14">
                    <div>
                      <span className="material-symbols-outlined fs-20 me-1 mt-1 text-secondary">
                        comment
                      </span>
                    </div>
                    <div className="flex-fill">{comment.comment}</div>
                  </div>

                  <div className="d-flex bg-gray-FAF justify-content-between fs-12 px-3 py-2">
                    <div className="text-light">
                      {formatDate(comment.createdAt.toString().slice(0, 10))}
                    </div>
                    <div className="d-flex align-items-center text-base">
                      <span className="material-symbols-outlined fs-18 me-1">
                        person
                      </span>
                      <span>{UserNameResolver(comment.createdBy)}</span>
                    </div>
                  </div>
                </div>
              )
            )}

            <div className="mt-4">
              <label
                htmlFor="newNotes"
                className="form-label fs-12 fw-semibold mb-1"
              >
                New Comments:
              </label>
              <textarea
                name="interventionComment"
                className="form-control form-control-sm"
                id="interventionComment"
                placeholder="Write here..."
                rows={2}
                onChange={(e) => handleInterventionInputChange(e)}
              ></textarea>
            </div>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary rounded-pill px-6 my-0"
              onClick={() => setInterventionModalShow(false)}
            >
              Discard
            </button>
            <button
              type="button"
              className="btn btn-sm btn-secondary rounded-pill px-6 my-0"
              onClick={(e) => interventionCommentSubmit(e)}
              disabled={interventionCommentForm?.interventionComment === '' ? true : false}
            >
              Submit
            </button>
          </Modal.Footer>
        </>
      </Modal>

      {/* Goal History */}

      <Modal
        show={goalHistoryModalShow}
        onHide={() => setgoalHistoryModalShow(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <>
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              History
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h3 className="fs-16">Goal: {selectedGoal?.goal?.goal}</h3>
            {goalHistory?.map(
              (history: ViewPatientGoalHistory, historyIndex: number) => (
                <div key={historyIndex}>
                  <div className="border rounded mt-4">
                    <div className="d-flex align-items-start px-3 py-2 fs-14">
                      <div className="flex-fill">
                        {history.fieldChanged === pgiConstants.CREATED_BY
                          ? `Created By ${history.newValue}`
                          : `Updated ${history.fieldChanged}: ${history.fieldChanged === "status"
                            ? workflowStatesCache.get(history.oldValue)
                            : history.fieldChanged === "due date"
                              ? formatDate(history.oldValue)
                              : history.oldValue
                          } ->
                          ${history.fieldChanged === "status"
                            ? workflowStatesCache.get(history.newValue)
                            : history.fieldChanged === "due date"
                              ? formatDate(history.newValue)
                              : history.newValue
                          }`}
                      </div>
                    </div>
                    <div className="d-flex bg-gray-FAF justify-content-between fs-12 px-3 py-2">
                      <div className="text-light">
                        {formatDate(history?.createdAt?.toString().slice(0, 10))}
                      </div>
                      <div className="d-flex align-items-center text-base">
                        <span className="material-symbols-outlined fs-18 me-1">
                          person
                        </span>
                        <span>{history.createdByUser}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            )}
          </Modal.Body>
        </>
      </Modal>

      {/* Intervention History */}

      <Modal
        show={interventionHistoryModalShow}
        onHide={() => setInterventionHistoryModalShow(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <>
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              History
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h3 className="fs-16">
              Intervention : {selectedIntervention?.intervention?.intervention}
            </h3>
            {interventionHistory?.map(
              (history: ViewPatientInterventionHistory, historyIndex: number) => (
                <div key={historyIndex}>
                  <div className="border rounded mt-4">
                    <div className="d-flex align-items-start px-3 py-2 fs-14">
                      <div className="flex-fill">
                        {history.fieldChanged === pgiConstants.CREATED_BY
                          ? `Created By ${history.newValue}`
                          : `Updated ${history.fieldChanged}: ${history.fieldChanged === "status"
                            ? workflowStatesCache.get(history.oldValue)
                            : history.fieldChanged === "due date"
                              ? formatDate(history.oldValue)
                              : history.fieldChanged === "assignee" ? userCache.get(history.oldValue) : history.oldValue
                          } ->
                          ${history.fieldChanged === "status"
                            ? workflowStatesCache.get(history.newValue)
                            : history.fieldChanged === "due date"
                              ? formatDate(history.newValue)
                              : history.fieldChanged === "assignee" ? userCache.get(history.newValue) : history.newValue
                          }`}
                      </div>
                    </div>
                    <div className="d-flex bg-gray-FAF justify-content-between fs-12 px-3 py-2">
                      <div className="text-light">
                        {formatDate(history?.createdAt?.toString().slice(0, 10))}
                      </div>
                      <div className="d-flex align-items-center text-base">
                        <span className="material-symbols-outlined fs-18 me-1">
                          person
                        </span>
                        <span>{history.createdByUser}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            )}
          </Modal.Body>
        </>
      </Modal>

      <OffCanvas
        title={
          offCanvasComp === "addPGI"
            ? "Add PGI"
            : offCanvasComp === "editGoal"
              ? "Edit Goal"
              : "Edit Intervention"
        }
        subTitle={
          offCanvasComp === "addPGI"
            ? "Add problem based on the interaction"
            : false
        }
        offCanvasShow={offCanvasShow}
        setOffCanvasShow={setOffCanvasShow}
      >
        {offCanvasComp === "addPGI" && (
          <AddPGI
            patientId={patientId}
            onSubmitPgi={handlerAddUpdatePgiForm}
            setOffCanvasShow={setOffCanvasShow}
            loadPgis={getPgi}
          />
        )}
        {offCanvasComp === "editGoal" && (
          <EditGoal
            selectedGoal={selectedGoal}
            onUpdateGoal={handlerAddUpdatePgiForm}
            setOffCanvasShow={setOffCanvasShow}
            loadPgis={getPgi}
          />
        )}
        {offCanvasComp === "editIntervention" && (
          <EditIntervention
            selectedIntervention={selectedIntervention}
            onUpdateIntervention={handlerAddUpdatePgiForm}
            setOffCanvasShow={setOffCanvasShow}
            loadPgis={getPgi}
          />
        )}
      </OffCanvas>
    </>
  );
};

//intervention popover
const interventionPopover = (interventionComment: PatientInterventionComment[]) => {
  return (
    <Popover id="popover-basic">
      <Popover.Body>
        {interventionComment?.map((comment: PatientInterventionComment, commentIndex: number) => (
          <div className="bg-white border rounded max-h-75" key={commentIndex}>
            <div className="d-flex align-items-start px-3 py-2 fs-12">
              <div>
                <span className="material-symbols-outlined fs-20 me-1 mt-1 text-secondary">
                  comment
                </span>
              </div>
              <div className="flex-fill">{comment.comment}</div>
            </div>
            <div className="d-flex bg-gray-FAF justify-content-between fs-11 px-3 py-2">
              <div className="text-light">{formatDate(comment.createdAt.toString().slice(0, 10))}</div>
              <div className="d-flex align-items-center text-base">
                <span className="material-symbols-outlined fs-18 me-1">person</span>
                <span>{UserNameResolver(comment.createdBy)}</span>
              </div>
            </div>
          </div>
        ))}
      </Popover.Body>
    </Popover>
  );
};

// goal Popover
const goalPopover = (goalComment: PatientGoalComment[]) => {
  return (
    <Popover id="popover-basic">
      <Popover.Body>
        {goalComment?.map((comment: PatientGoalComment, commentIndex: number) => (
          <div className="bg-white border rounded max-h-15" key={commentIndex}>
            <div className="d-flex align-items-start px-3 py-2 fs-12">
              <div>
                <span className="material-symbols-outlined fs-20 me-1 mt-1 text-secondary">
                  comment
                </span>
              </div>
              <div className="flex-fill">
                {comment.comment}
              </div>
            </div>
            <div className="d-flex bg-gray-FAF justify-content-between fs-11 px-3 py-2">
              <div className="text-light">
                {formatDate(comment.createdAt.toString().slice(0, 10))}
              </div>
              <div className="d-flex align-items-center text-base">
                <span className="material-symbols-outlined fs-18 me-1">
                  person
                </span>
                <span>{UserNameResolver(comment.createdBy)}</span>
              </div>
            </div>
          </div>
        ))}
      </Popover.Body>
    </Popover>
  );
};
